// Fixed useDevicePhotoManager Hook - Proper numbered photo handling with bug fixes
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export const useDevicePhotoManager = ({
    deviceId,
    photos: propPhotos,
    setPhotos: setPropPhotos,
    isEditing,
    orgId,
    config,
    maxPhotos = 6,
    onSubmit,
    onComplete,
}) => {
    const { t } = useTranslation();
    
    // Generate unique ID for photos
    const generatePhotoId = () => `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const [photos, setPhotos] = useState(() => {
        if (propPhotos?.length > 0) {
            // Ensure all photos have a photoNumber assigned
            return propPhotos.map((photo, index) => ({
                ...photo,
                photoNumber: photo.photoNumber || (index + 1),
            }));
        }
        return [{
            id: generatePhotoId(),
            photoNumber: 1,
            file: null,
            fileName: '',
            preview: null,
            isExisting: false,
            isUploaded: false,
            needsReplacement: false,
            serverId: null,
        }];
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [alert, setAlert] = useState({
        open: false,
        message: '',
        severity: 'info',
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        photoId: null,
    });

    // Get all used photo numbers
    const getUsedPhotoNumbers = useCallback((currentPhotos) => {
        return currentPhotos
            .filter(photo => photo.photoNumber && photo.photoNumber >= 1 && photo.photoNumber <= maxPhotos)
            .map(photo => photo.photoNumber);
    }, [maxPhotos]);

    // Get available photo numbers (1-maxPhotos)
    const getAvailablePhotoNumbers = useCallback((currentPhotos) => {
        const usedNumbers = getUsedPhotoNumbers(currentPhotos);
        const available = [];
        for (let i = 1; i <= maxPhotos; i++) {
            if (!usedNumbers.includes(i)) {
                available.push(i);
            }
        }
        return available.sort((a, b) => a - b);
    }, [maxPhotos, getUsedPhotoNumbers]);

    // Synchronize photos with propPhotos when they change externally (but only on initial load in edit mode)
    useEffect(() => {
        if (isEditing && propPhotos && Array.isArray(propPhotos) && propPhotos.length > 0) {
            // Only update if propPhotos is significantly different and we're in edit mode
            const propPhotoIds = propPhotos.map(p => p.id).sort();
            const currentPhotoIds = photos.map(p => p.id).sort();
            const isDifferent = propPhotoIds.length !== currentPhotoIds.length || 
                               propPhotoIds.some((id, index) => id !== currentPhotoIds[index]);
            
            if (isDifferent) {
                setPhotos(propPhotos);
            }
        }
    }, [isEditing]);

    // Update propPhotos when photos change (only with photos that have content)
    const updatePropPhotos = useCallback((updatedPhotos) => {
        if (setPropPhotos) {
            // Only send photos with actual content (file or uploaded) to parent
            const photosToUpdate = updatedPhotos.filter(photo => photo.file || photo.isUploaded);
            setPropPhotos(photosToUpdate);
            // Note: We do NOT sync back to local photos state to preserve empty slots
        }
    }, [setPropPhotos]);

    // Load existing photos when in edit mode
    useEffect(() => {
        if (isEditing && deviceId && orgId && !isLoading) {
            fetchExistingPhotos();
        }
        
        // Cleanup function for blob URLs
        return () => {
            photos.forEach((photo) => {
                if (photo.preview && !photo.isExisting && photo.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(photo.preview);
                }
            });
        };
    }, [deviceId, isEditing, orgId]);

    const fetchExistingPhotos = async (deviceIdParam) => {
        const fetchId = deviceIdParam || deviceId;
        if (!fetchId || isNaN(parseInt(fetchId))) {
            console.error('Invalid Device ID provided');
            setAlert({
                open: true,
                message: t('Invalid Device ID provided'),
                severity: 'error',
            });
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.get(`${config.baseURL}/device/${fetchId}`, {
                withCredentials: true,
            });

            const deviceData = response.data?.data || response.data || {};
            console.log('Fetched Device Data:', deviceData);

            const documents = Array.isArray(deviceData.documents)
                ? deviceData.documents
                : [];
            const installationPhotos = documents.filter(
                (doc) => doc.name && /^InstallationPhoto(\d*)$/.test(doc.name)
            );

            console.log('Found numbered installation photos:', installationPhotos);

            if (installationPhotos.length > 0) {
                // Sort by photo number extracted from name
                const photoNumberFromName = (name) => {
                    const parsedNumber = parseInt(String(name || "").replace("InstallationPhoto", ""), 10);
                    return Number.isFinite(parsedNumber) && parsedNumber > 0 ? parsedNumber : 0;
                };
                const sortedPhotos = installationPhotos.sort(
                    (a, b) => photoNumberFromName(a.name) - photoNumberFromName(b.name)
                );

                const existingPhotos = sortedPhotos.map((photo, index) => {
                    const parsedNumber = parseInt(photo.name.replace('InstallationPhoto', ''), 10);
                    const photoNumber = Number.isFinite(parsedNumber) && parsedNumber > 0
                        ? parsedNumber
                        : index + 1;
                    const photoUrl = `${config.baseURL}/upload/image?fileName=${encodeURIComponent(photo.location)}`;

                    return {
                        id: generatePhotoId(),
                        photoNumber: photoNumber,
                        fileName: photo.location ? photo.location.split('/').pop() : '',
                        serverId: photo.id,
                        isExisting: true,
                        isUploaded: true,
                        createdAt: photo.createdAt,
                        updatedAt: photo.updatedAt,
                        preview: photoUrl,
                        needsReplacement: false,
                        file: null,
                    };
                });

                console.log('Setting existing photos:', existingPhotos);
                setPhotos(existingPhotos);
                updatePropPhotos(existingPhotos);
            } else {
                console.log('No installation photos found');
                setPhotos((currentPhotos) => {
                    const hasLocalPhoto = currentPhotos.some(
                        (photo) => photo.file || photo.preview || photo.isUploaded
                    );
                    return hasLocalPhoto ? currentPhotos : [{
                        id: generatePhotoId(),
                        photoNumber: 1,
                        file: null,
                        fileName: '',
                        preview: null,
                        isExisting: false,
                        isUploaded: false,
                        needsReplacement: false,
                        serverId: null,
                    }];
                });
            }
        } catch (error) {
            console.error('Error fetching device photos:', error);
            setAlert({
                open: true,
                message: t(`Failed to load existing photos: ${error.response?.data?.error || error.message}`),
                severity: 'error',
            });

            setPhotos((currentPhotos) => {
                const hasLocalPhoto = currentPhotos.some(
                    (photo) => photo.file || photo.preview || photo.isUploaded
                );
                if (hasLocalPhoto) return currentPhotos;
                return [{
                    id: generatePhotoId(),
                    photoNumber: 1,
                    file: null,
                    fileName: '',
                    preview: null,
                    isExisting: false,
                    isUploaded: false,
                    needsReplacement: false,
                    serverId: null,
                }];
            });
        } finally {
            setIsLoading(false);
        }
    };

    const validatePhoto = (photo) => {
        const newErrors = {};
        if (!photo.file && !photo.isUploaded) {
            newErrors.file = "Photo is required";
        }
        return newErrors;
    };

    const validateAllPhotos = () => {
        const newErrors = {};
        photos.forEach((photo) => {
            if (touched[photo.id]) {
                const photoErrors = validatePhoto(photo);
                if (Object.keys(photoErrors).length > 0) {
                    newErrors[photo.id] = photoErrors;
                }
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        if (Object.keys(touched).length > 0) {
            validateAllPhotos();
        }
    }, [photos, touched]);

    const canAddPhoto = () => {
        // Can add if we haven't reached max photos and there are available numbers
        const availableNumbers = getAvailablePhotoNumbers(photos);
        return photos.length < maxPhotos && availableNumbers.length > 0;
    };

    const handleAddPhoto = () => {
        if (!canAddPhoto()) {
            setAlert({
                open: true,
                message: t('Cannot add more photos. You have reached the maximum limit.'),
                severity: 'warning',
            });
            return;
        }

        const availableNumbers = getAvailablePhotoNumbers(photos);
        if (availableNumbers.length === 0) {
            setAlert({
                open: true,
                message: t('No available photo slots. Please remove a photo first.'),
                severity: 'warning',
            });
            return;
        }

        const nextNumber = Math.min(...availableNumbers);
        const newPhoto = {
            id: generatePhotoId(),
            photoNumber: nextNumber,
            file: null,
            fileName: '',
            preview: null,
            isExisting: false,
            isUploaded: false,
            needsReplacement: false,
            serverId: null,
        };
        
        const updatedPhotos = [...photos, newPhoto].sort((a, b) => a.photoNumber - b.photoNumber);
        setPhotos(updatedPhotos);
        updatePropPhotos(updatedPhotos);
    };

    const handleRemovePhotoBox = (index) => {
        if (photos.length <= 1) {
            setAlert({
                open: true,
                message: t('At least one photo slot is required'),
                severity: 'warning',
            });
            return;
        }

        const photoToRemove = photos[index];
        if (photoToRemove.isExisting) {
            setConfirmModal({
                open: true,
                photoId: photoToRemove.id,
            });
            return;
        }

        removePhotoById(photoToRemove.id);
    };

    const handleRemovePhoto = (photoId) => {
        const photoToRemove = photos.find((photo) => photo.id === photoId);

        if (!photoToRemove) {
            console.error('Photo not found:', photoId);
            return;
        }

        if (photoToRemove.isExisting) {
            setConfirmModal({
                open: true,
                photoId: photoId,
            });
            return;
        }

        // For new photos with content, clear the file but keep the slot
        if (photoToRemove.file || photoToRemove.preview) {
            const updatedPhotos = photos.map((photo) =>
                photo.id === photoId
                    ? {
                        ...photo,
                        file: null,
                        fileName: '',
                        preview: null,
                        needsReplacement: false,
                    }
                    : photo
            );

            setPhotos(updatedPhotos);
            updatePropPhotos(updatedPhotos);

            // Clean up blob URL
            if (photoToRemove.preview && photoToRemove.preview.startsWith('blob:')) {
                URL.revokeObjectURL(photoToRemove.preview);
            }
        } else {
            // For empty slots, remove the photo completely
            removePhotoById(photoId);
        }

        // Clear errors for this photo
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[photoId];
            return newErrors;
        });
        setTouched(prev => {
            const newTouched = { ...prev };
            delete newTouched[photoId];
            return newTouched;
        });
    };

    const handleCancelRemove = () => {
        setConfirmModal({ open: false, photoId: null });
    };

    const handleConfirmRemove = async () => {
        const photoId = confirmModal.photoId;
        const photoToRemove = photos.find((photo) => photo.id === photoId);

        if (!photoToRemove) {
            console.error('Photo not found for removal:', photoId);
            setConfirmModal({ open: false, photoId: null });
            return;
        }

        if (photoToRemove.isExisting && photoToRemove.serverId) {
            try {
                // Delete using the numbered photo name
                const photoName = `InstallationPhoto${photoToRemove.photoNumber}`;
                await axios.delete(`${config.baseURL}/upload/image`, {
                    params: {
                        name: photoName,
                        associateEntityId: deviceId,
                        associatedWith: 'Device'
                    },
                    withCredentials: true,
                });

                console.log(`Successfully deleted photo: ${photoName}`);
            } catch (error) {
                console.error('Error deleting photo:', error);
                setAlert({
                    open: true,
                    message: t(`Failed to delete photo: ${error.response?.data?.error || error.message}`),
                    severity: 'error',
                });
                setConfirmModal({ open: false, photoId: null });
                return;
            }
        }

        removePhotoById(photoId);
        setConfirmModal({ open: false, photoId: null });
    };

    const removePhotoById = (photoId) => {
        const photoToRemove = photos.find((photo) => photo.id === photoId);

        if (photoToRemove && photoToRemove.preview && !photoToRemove.isExisting && photoToRemove.preview.startsWith('blob:')) {
            URL.revokeObjectURL(photoToRemove.preview);
        }

        const newPhotos = photos.filter((photo) => photo.id !== photoId);
        
        // Ensure we always have at least one empty photo slot
        const finalPhotos = newPhotos.length > 0 ? newPhotos : [{
            id: generatePhotoId(),
            photoNumber: 1,
            file: null,
            fileName: '',
            preview: null,
            isExisting: false,
            isUploaded: false,
            needsReplacement: false,
            serverId: null,
        }];

        setPhotos(finalPhotos);
        updatePropPhotos(finalPhotos);

        // Clear errors and touched state
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[photoId];
            return newErrors;
        });
        setTouched((prev) => {
            const newTouched = { ...prev };
            delete newTouched[photoId];
            return newTouched;
        });
    };

    const handlePhotoUpload = (photoId, event) => {
        if (!event.target.files || !event.target.files[0]) {
            return;
        }

        const file = event.target.files[0];
        const allowedFileTypes = ['image/jpeg', 'image/jpg', 'image/png'];

        if (!allowedFileTypes.includes(file.type)) {
            setAlert({
                open: true,
                message: t('Invalid file type. Only JPG, JPEG, and PNG are allowed.'),
                severity: 'error',
            });
            setTouched((prev) => ({
                ...prev,
                [photoId]: { ...prev[photoId], file: true },
            }));
            setErrors((prev) => ({
                ...prev,
                [photoId]: { file: t('Invalid file type. Only JPG, JPEG, and PNG are allowed.') },
            }));
            event.target.value = '';
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setAlert({
                open: true,
                message: t('File size exceeds 10MB limit'),
                severity: 'warning',
            });
            event.target.value = '';
            return;
        }

        const fileUrl = URL.createObjectURL(file);
        const photoToUpdate = photos.find(photo => photo.id === photoId);

        if (!photoToUpdate) {
            console.error('Photo not found for upload:', photoId);
            URL.revokeObjectURL(fileUrl);
            event.target.value = '';
            return;
        }

        const updatedPhotos = photos.map((photo) => {
            if (photo.id === photoId) {
                // Clean up previous blob URL
                if (photo.preview && !photo.isExisting && photo.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(photo.preview);
                }
                return {
                    ...photo,
                    file,
                    fileName: file.name,
                    preview: fileUrl,
                    isUploaded: false,
                    needsReplacement: photo.isExisting ? true : false,
                };
            }
            return photo;
        });

        setPhotos(updatedPhotos);
        updatePropPhotos(updatedPhotos);

        // Update touched and clear errors
        setTouched((prev) => ({
            ...prev,
            [photoId]: { ...prev[photoId], file: true },
        }));

        setErrors((prev) => {
            const newErrors = { ...prev };
            const updatedPhoto = updatedPhotos.find(p => p.id === photoId);
            if (updatedPhoto) {
                const photoErrors = validatePhoto(updatedPhoto);
                if (Object.keys(photoErrors).length === 0) {
                    delete newErrors[photoId];
                } else {
                    newErrors[photoId] = photoErrors;
                }
            }
            return newErrors;
        });

        event.target.value = '';
    };

    const handleDownload = async (photo) => {
        if (!photo.preview) {
            setAlert({
                open: true,
                message: t('No image available for download'),
                severity: 'warning',
            });
            return;
        }

        try {
            let blobUrl = photo.preview;
            let fileName = photo.fileName || `installation-photo-${photo.photoNumber}.jpg`;

            // If it's a server URL, fetch the blob
            if (!photo.preview.startsWith('blob:')) {
                try {
                    const response = await axios.get(photo.preview, {
                        responseType: 'blob',
                        withCredentials: true,
                    });
                    const blob = new Blob([response.data], { type: response.data.type || 'image/jpeg' });
                    blobUrl = URL.createObjectURL(blob);
                } catch (fetchError) {
                    console.error('Error fetching image for download:', fetchError);
                    setAlert({
                        open: true,
                        message: t('Failed to download photo. Please try again.'),
                        severity: 'error',
                    });
                    return;
                }
            }

            // Create and trigger download
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up blob URL if we created it
            if (blobUrl !== photo.preview) {
                URL.revokeObjectURL(blobUrl);
            }
        } catch (error) {
            console.error('Error downloading photo:', error);
            setAlert({
                open: true,
                message: t(`Failed to download photo: ${error.message}`),
                severity: 'error',
            });
        }
    };

    const handleSubmitPhotos = async (deviceIdParam, photosSnapshot) => {
        const currentDeviceId = deviceIdParam || deviceId;
        // Use the passed snapshot if provided, otherwise fall back to current state
        const photosToProcess = photosSnapshot || photos;

        if (!currentDeviceId) {
            setAlert({
                open: true,
                message: t('Device ID is required to upload photos'),
                severity: 'error',
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const photosToUpload = photosToProcess.filter(
                photo => (photo.file && !photo.isUploaded) || photo.needsReplacement
            );

            console.log('Photos to upload:', photosToUpload);

            if (photosToUpload.length === 0) {
                console.log('No photos to upload');
                setIsSubmitting(false);
                return;
            }

            for (const photo of photosToUpload) {
                if (!photo.file) {
                    console.warn('Skipping photo without file:', photo.id);
                    continue;
                }

                const formData = new FormData();
                formData.append('image', photo.file);

                // Use numbered photo name
                const photoName = `InstallationPhoto${photo.photoNumber}`;

                try {
                    const response = await axios.post(
                        `${config.baseURL}/upload/image?name=${encodeURIComponent(photoName)}&associateEntityId=${currentDeviceId}&associatedWith=Device&type=document`,
                        formData,
                        {
                            withCredentials: true,
                        }
                    );

                    console.log(`Photo upload response for ${photoName}:`, response.data);
                    const uploaded = response.data?.data || response.data || {};
                    const location = uploaded.location;

                    // Update photo state immediately after successful upload
                    setPhotos(prevPhotos => {
                        const updatedPhotos = prevPhotos.map(p =>
                            p.id === photo.id
                                ? {
                                    ...p,
                                    isUploaded: true,
                                    isExisting: true,
                                    needsReplacement: false,
                                    fileName: location?.split('/').pop() || p.fileName || photoName,
                                    serverId: uploaded.id || p.serverId,
                                    preview: location
                                        ? `${config.baseURL}/upload/image?fileName=${encodeURIComponent(location)}`
                                        : p.preview,
                                    createdAt: uploaded.createdAt || new Date().toISOString(),
                                    updatedAt: uploaded.updatedAt || new Date().toISOString(),
                                }
                                : p
                        );
                        updatePropPhotos(updatedPhotos);
                        return updatedPhotos;
                    });
                } catch (uploadError) {
                    console.error(`Failed to upload photo ${photoName}:`, uploadError);
                    setAlert({
                        open: true,
                        message: t(`Failed to upload photo ${photo.photoNumber}: ${uploadError.response?.data?.error || uploadError.message}`),
                        severity: 'error',
                    });
                    throw uploadError;
                }
            }

            // Refresh photos after upload to sync with server state
            if (currentDeviceId) {
                setTimeout(() => {
                    fetchExistingPhotos(currentDeviceId);
                }, 1000); // Small delay to ensure backend processing
            }

            setAlert({
                open: true,
                message: t('Photos uploaded successfully'),
                severity: 'success',
            });

        } catch (error) {
            console.error('Error uploading photos:', error);
            setAlert({
                open: true,
                message: t(`Failed to upload photos: ${error.response?.data?.error || error.message}`),
                severity: 'error',
            });
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSave = async (isDetailsFormValid, triggerFormValidation) => {
        if (!isDetailsFormValid) {
            // Trigger form field validation to show red borders and error messages
            if (typeof triggerFormValidation === 'function') {
                triggerFormValidation();
            }
            // setAlert({
            //     open: true,
            //     message: t('Please fill in all required fields (Device ID and Status)'),
            //     severity: 'error',
            // });
            return;
        }

        const isValid = validateAllPhotos();
        if (!isValid) {
            setAlert({
                open: true,
                message: t('Please correct all photo validation errors'),
                severity: 'error',
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await onSubmit();
            const currentDeviceId = result || deviceId;

            if (!currentDeviceId) {
                setAlert({
                    open: true,
                    message: t('Failed to save device details'),
                    severity: 'error',
                });
                return;
            }

            // Capture the current photos snapshot AFTER onSubmit resolves,
            // so we have the latest state (not a stale closure)
            const currentPhotos = photos;
            const photosToUpload = currentPhotos.filter(
                (photo) => (photo.file && !photo.isUploaded) || photo.needsReplacement
            );

            if (photosToUpload.length > 0) {
                await handleSubmitPhotos(currentDeviceId, currentPhotos);
            }

            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            console.error('Error during device save process:', error);
            // 400 errors are already handled as field-level errors in the form (e.g. duplicate device ID)
            // so we don't need to show an additional generic snackbar for them
            if (error?.response?.status !== 400) {
                setAlert({
                    open: true,
                    message: t(`Failed to save: ${error.response?.data?.error || error.message}`),
                    severity: 'error',
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        photos,
        isLoading,
        isSubmitting,
        alert,
        errors,
        touched,
        confirmModal,
        availableNumbers: getAvailablePhotoNumbers(photos),
        setAlert,
        setTouched,
        canAddPhoto,
        handleAddPhoto,
        handleRemovePhotoBox,
        handleRemovePhoto,
        handleCancelRemove,
        handleConfirmRemove,
        handlePhotoUpload,
        handleDownload,
        handleSubmitPhotos,
        handleSave,
    };
};